export type SpeechEngineErrorCode = 'not-allowed' | 'no-speech' | 'network' | 'unsupported' | 'other';

export interface SpeechEngineResult {
  transcript: string;
  isFinal: boolean;
}

export interface SpeechEngineError {
  code: SpeechEngineErrorCode;
  message: string;
}

export interface SpeechEngine {
  isSupported(): boolean;
  start(): void;
  stop(): void;
  onResult(cb: (result: SpeechEngineResult) => void): void;
  onEnd(cb: () => void): void;
  onError(cb: (error: SpeechEngineError) => void): void;
}

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: { readonly transcript: string };
}

export interface SpeechRecognitionResultEventLike {
  readonly resultIndex: number;
  readonly results: ArrayLike<SpeechRecognitionResultLike>;
}

export interface SpeechRecognitionErrorEventLike {
  readonly error: string;
  readonly message?: string;
}

export interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export const DEFAULT_SPEECH_LANG = 'en-US';

export function resolveSpeechRecognition(
  scope: unknown = globalThis
): SpeechRecognitionConstructor | null {
  const candidate = scope as {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  } | null;
  return candidate?.SpeechRecognition ?? candidate?.webkitSpeechRecognition ?? null;
}

export function isSpeechRecognitionSupported(scope?: unknown): boolean {
  return resolveSpeechRecognition(scope) !== null;
}

function toEngineError(event: SpeechRecognitionErrorEventLike): SpeechEngineError {
  const message = event.message || event.error;
  switch (event.error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return { code: 'not-allowed', message };
    case 'no-speech':
      return { code: 'no-speech', message };
    case 'network':
      return { code: 'network', message };
    default:
      return { code: 'other', message };
  }
}

function noop() {}

export class WebSpeechEngine implements SpeechEngine {
  private recognition: SpeechRecognitionLike | null = null;
  private resultCallback: (result: SpeechEngineResult) => void = noop;
  private endCallback: () => void = noop;
  private errorCallback: (error: SpeechEngineError) => void = noop;

  constructor(
    private readonly Recognition: SpeechRecognitionConstructor | null,
    private readonly lang: string = DEFAULT_SPEECH_LANG
  ) {}

  isSupported(): boolean {
    return this.Recognition !== null;
  }

  start(): void {
    if (!this.Recognition) {
      this.errorCallback({ code: 'unsupported', message: 'Speech recognition is not supported in this browser' });
      return;
    }

    this.detach()?.abort();

    const recognition = new this.Recognition();
    recognition.lang = this.lang;
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.onresult = (event) => this.handleResult(event);
    recognition.onerror = (event) => {
      if (event.error === 'aborted') return;
      this.errorCallback(toEngineError(event));
    };
    recognition.onend = () => {
      if (this.recognition !== recognition) return;
      this.recognition = null;
      this.endCallback();
    };
    this.recognition = recognition;

    try {
      recognition.start();
    } catch (error) {
      this.detach();
      this.errorCallback({
        code: 'other',
        message: error instanceof Error ? error.message : 'Speech recognition failed to start',
      });
    }
  }

  stop(): void {
    this.recognition?.stop();
  }

  onResult(cb: (result: SpeechEngineResult) => void): void {
    this.resultCallback = cb;
  }

  onEnd(cb: () => void): void {
    this.endCallback = cb;
  }

  onError(cb: (error: SpeechEngineError) => void): void {
    this.errorCallback = cb;
  }

  private detach(): SpeechRecognitionLike | null {
    const recognition = this.recognition;
    if (recognition) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
    }
    this.recognition = null;
    return recognition;
  }

  private handleResult(event: SpeechRecognitionResultEventLike): void {
    let interim = '';
    for (let index = event.resultIndex; index < event.results.length; index += 1) {
      const result = event.results[index];
      const transcript = result[0]?.transcript ?? '';
      if (result.isFinal) {
        this.resultCallback({ transcript, isFinal: true });
      } else {
        interim += transcript;
      }
    }
    this.resultCallback({ transcript: interim, isFinal: false });
  }
}

export interface CreateSpeechEngineOptions {
  Recognition?: SpeechRecognitionConstructor | null;
  lang?: string;
}

export function createSpeechEngine(options: CreateSpeechEngineOptions = {}): SpeechEngine {
  const Recognition = options.Recognition === undefined ? resolveSpeechRecognition() : options.Recognition;
  return new WebSpeechEngine(Recognition, options.lang);
}
